import React from "react";
import PropTypes from "prop-types";
import { Slider, Label, Input, makeStyles } from "@fluentui/react-components";
import { useIntl } from "react-intl";

const useStyles = makeStyles({
  sliderInputs: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    "& input": {
      appearance: "textfield", // default behavior for number inputs
      // Apply styles for removing number input arrows in Webkit-based browsers
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        appearance: "none",
        margin: 0
      },
      // For Firefox (removes spinner arrows)
      '&[type="number"]': {
        "-moz-appearance": "textfield"
      }
    },
    gap: "var(--Font-size-100, 10px)"
  }
});

export function SliderWithLabel({
  labelId,
  defaultLabel,
  value,
  onChange,
  min,
  max,
  sliderId,
  ariaValueTextFormatter,
  step = undefined
}) {
  const intl = useIntl();
  const styles = useStyles();

  const handleChange = (e, data) => {
    let newValue = data.value;

    if (typeof newValue === "string") {
      newValue = parseFloat(newValue);
    }

    if (newValue === null || newValue < min) {
      newValue = min;
    } else if (newValue > max) {
      newValue = max;
    }

    onChange(e, {
      value: newValue,
      ...data
    });
  };

  return (
    <>
      <Label htmlFor={sliderId}>
        {intl.formatMessage({
          id: labelId,
          defaultMessage: defaultLabel
        })}
      </Label>
      <div className={styles.sliderInputs}>
        <Input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={handleChange}
          id={sliderId}
          aria-valuetext={ariaValueTextFormatter(value)}
          appearance="filled-darker"
        />
        <Slider
          aria-valuetext={ariaValueTextFormatter(value)}
          value={value}
          min={min}
          max={max}
          onChange={handleChange}
          id={sliderId}
          step={step}
        />
      </div>
    </>
  );
}

SliderWithLabel.propTypes = {
  labelId: PropTypes.string.isRequired,
  defaultLabel: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  sliderId: PropTypes.string.isRequired,
  ariaValueTextFormatter: PropTypes.func.isRequired,
  step: PropTypes.number
};

// Voice Volume Slider
export function VoiceVolumeSlider({ voiceVolume, onVoiceVolumeChange }) {
  return (
    <SliderWithLabel
      labelId="room-settings-sidebar.voice-volume"
      defaultLabel="Voice Volume"
      value={voiceVolume}
      onChange={onVoiceVolumeChange}
      min={0}
      max={200}
      sliderId="voice-volume-slider"
      ariaValueTextFormatter={value => `${value}%`}
    />
  );
}

VoiceVolumeSlider.propTypes = {
  voiceVolume: PropTypes.number.isRequired,
  onVoiceVolumeChange: PropTypes.func.isRequired
};

export function MediaVolumeSlider({ mediaVolume, onMediaVolumeChange }) {
  return (
    <SliderWithLabel
      labelId="room-settings-sidebar.media-volume"
      defaultLabel="Media Volume"
      value={mediaVolume}
      onChange={onMediaVolumeChange}
      min={0}
      max={200}
      sliderId="media-volume-slider"
      ariaValueTextFormatter={value => `${value}%`}
    />
  );
}

MediaVolumeSlider.propTypes = {
  mediaVolume: PropTypes.number.isRequired,
  onMediaVolumeChange: PropTypes.func.isRequired
};

export function SpecialEffectsVolumeSlider({ specialEffectsVolume, onSpecialEffectsVolumeChange }) {
  return (
    <SliderWithLabel
      labelId="room-settings-sidebar.special-effects-volume"
      defaultLabel="Special Effects Volume"
      value={specialEffectsVolume}
      onChange={onSpecialEffectsVolumeChange}
      min={0}
      max={200}
      sliderId="special-effects-volume-slider"
      ariaValueTextFormatter={value => `${value}%`}
    />
  );
}

SpecialEffectsVolumeSlider.propTypes = {
  specialEffectsVolume: PropTypes.number.isRequired,
  onSpecialEffectsVolumeChange: PropTypes.func.isRequired
};

export function AudioClippingThresholdSlider({ audioClippingThreshold, onAudioClippingThresholdChange }) {
  const getSliderValue = () => Math.round(audioClippingThreshold * 1000);
  const setSliderValue = value => onAudioClippingThresholdChange(value / 1000);

  return (
    <SliderWithLabel
      labelId="room-settings-sidebar.audio-clipping-threshold"
      defaultLabel="Audio Clipping Threshold"
      value={getSliderValue()}
      onChange={(_, data) => setSliderValue(data.value)}
      min={0}
      max={100}
      sliderId="audio-clipping-threshold-slider"
      ariaValueTextFormatter={value => `${(value / 100).toFixed(3)}`}
      customValue={audioClippingThreshold}
    />
  );
}

AudioClippingThresholdSlider.propTypes = {
  audioClippingThreshold: PropTypes.number.isRequired,
  onAudioClippingThresholdChange: PropTypes.func.isRequired
};

export function SnapRotationDegreesSlider({ snapRotationDegrees, onSnapRotationDegreesChange }) {
  return (
    <SliderWithLabel
      labelId="preferences-screen.preference.snap-rotation-degrees"
      defaultLabel="Rotation (in degrees)"
      value={snapRotationDegrees}
      onChange={onSnapRotationDegreesChange}
      min={0}
      max={90}
      sliderId="snap-rotation-degrees-slider"
      ariaValueTextFormatter={value => `${value}°`}
    />
  );
}

SnapRotationDegreesSlider.propTypes = {
  snapRotationDegrees: PropTypes.number.isRequired,
  onSnapRotationDegreesChange: PropTypes.func.isRequired
};

export function MovementSpeedSlider({ movementSpeedModifier, onMovementSpeedModifierChange }) {
  return (
    <SliderWithLabel
      labelId="preferences-screen.preference.movement-speed-modifier"
      defaultLabel="Movement speed"
      value={movementSpeedModifier}
      onChange={onMovementSpeedModifierChange}
      min={0}
      max={2}
      step={0.1}
      sliderId="movement-speed-modifier-slider"
      ariaValueTextFormatter={value => `${value}x`}
    />
  );
}

MovementSpeedSlider.propTypes = {
  movementSpeedModifier: PropTypes.number.isRequired,
  onMovementSpeedModifierChange: PropTypes.func.isRequired
};

export function CursorSizeSlider({ cursorSize, onCursorSizeChange }) {
  return (
    <SliderWithLabel
      labelId="preferences-screen.preference.cursor-size"
      defaultLabel="Cursor Size"
      value={cursorSize}
      onChange={onCursorSizeChange}
      min={1}
      max={5}
      sliderId="cursor-size-slider"
      ariaValueTextFormatter={value => `${value}px`}
    />
  );
}

CursorSizeSlider.propTypes = {
  cursorSize: PropTypes.number.isRequired,
  onCursorSizeChange: PropTypes.func.isRequired
};

export function NametagVisibilityDistanceSlider({ nametagVisibilityDistance, onNametagVisibilityDistanceChange }) {
  return (
    <SliderWithLabel
      labelId="preferences-screen.preference.nametag-visibility-distance"
      defaultLabel="Nametag visibility distance"
      value={nametagVisibilityDistance}
      onChange={onNametagVisibilityDistanceChange}
      min={1}
      max={20}
      sliderId="nametag-visibility-distance-slider"
      ariaValueTextFormatter={value => `${value}px`}
    />
  );
}

NametagVisibilityDistanceSlider.propTypes = {
  nametagVisibilityDistance: PropTypes.number.isRequired,
  onNametagVisibilityDistanceChange: PropTypes.func.isRequired
};
